import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { KYC } from "./useProfileApi";
import { IconAvatar, IconVerify, IconUser, IconMail, IconLock, IconCheck, IconAlert } from "./icons";
import "./profile-theme.css";

export const PROFILE_ROUTES = {
  profile: "/account/me", // страница PresonalInfo
  kyc: "/account/kyc", // страница Kyc
};

const SPY_IDS = ["personal", "contacts", "security"];

/* ---------- тост (замена notistack на этих страницах) ---------- */
export function useToast() {
  const [state, setState] = useState({ text: "", kind: "ok", show: false });
  const timer = useRef();

  const notify = useCallback((text, kind = "ok") => {
    clearTimeout(timer.current);
    setState({ text, kind, show: true });
    timer.current = setTimeout(
      () => setState((s) => ({ ...s, show: false })),
      kind === "err" ? 5200 : 2800
    );
  }, []);

  useEffect(() => () => clearTimeout(timer.current), []);

  const toast = (
    <div
      className={`fx-toast${state.show ? " show" : ""}${state.kind === "err" ? " err" : ""}`}
      role="status"
      aria-live="polite"
    >
      {state.kind === "err" ? <IconAlert /> : <IconCheck />}
      <span>{state.text}</span>
    </div>
  );
  return { toast, notify };
}

/* ---------- каркас: шапка + левый рельс + контент ---------- */
export default function ProfileLayout({
  page, // "profile" | "kyc"
  header, // <Header hasMenu2 /> — страница передаёт сама, чтобы не гадать с путями импорта
  me, // объект из /users/me (может быть null)
  kycStatus, // KYC.* или null
  toast,
  floating, // fixed-элементы (savebar)
  children,
}) {
  const { t } = useTranslation();
  const { hash } = useLocation();
  const [spy, setSpy] = useState("personal");

  /* подсветка пункта рельса при скролле (только на странице профиля) */
  useEffect(() => {
    if (page !== "profile") return undefined;
    const els = SPY_IDS.map((id) => document.getElementById(id)).filter(Boolean);
    if (!els.length || typeof IntersectionObserver === "undefined") return undefined;
    const io = new IntersectionObserver(
      (entries) =>
        entries.forEach((e) => {
          if (e.isIntersecting) setSpy(e.target.id);
        }),
      { rootMargin: "-30% 0px -55% 0px" }
    );
    els.forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, [page]);

  /* переход с другой страницы по якорю (/personal-info#security) */
  useEffect(() => {
    if (!hash) return;
    const el = document.getElementById(hash.slice(1));
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  }, [hash, page]);

  const items = [
    { id: "verify", label: t("profile.nav.verify", "Верификация"), icon: <IconVerify />, to: PROFILE_ROUTES.kyc },
    { id: "personal", label: t("profile.nav.personal", "Личные данные"), icon: <IconUser />, to: { pathname: PROFILE_ROUTES.profile, hash: "#personal" } },
    { id: "contacts", label: t("profile.nav.contacts", "Контакты"), icon: <IconMail />, to: { pathname: PROFILE_ROUTES.profile, hash: "#contacts" } },
    { id: "security", label: t("profile.nav.security", "Безопасность"), icon: <IconLock />, to: { pathname: PROFILE_ROUTES.profile, hash: "#security" } },
  ];
  const activeId = page === "kyc" ? "verify" : spy;

  const onNavClick = (id) => (e) => {
    // на странице профиля якоря — просто плавный скролл без смены URL
    if (page === "profile" && SPY_IDS.includes(id)) {
      e.preventDefault();
      setSpy(id);
      document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  const fullName =
    [me?.name, me?.surname].filter(Boolean).join(" ") || me?.displayed_name || me?.email || "";

  const statusText = {
    [KYC.NONE]: t("profile.status.none", "Не верифицирован"),
    [KYC.PENDING]: t("profile.status.pending", "На проверке"),
    [KYC.VERIFIED]: t("profile.status.verified", "Верифицирован"),
    [KYC.REJECTED]: t("profile.status.rejected", "Отклонено"),
    [KYC.UNKNOWN]: t("profile.status.unknown", "Недоступно"),
  }[kycStatus];
  const statusTone = {
    [KYC.PENDING]: "warn",
    [KYC.VERIFIED]: "up",
    [KYC.REJECTED]: "down",
  }[kycStatus];

  return (
    <div className="fx-page">
      {header}
      <div className="fx-body">
        <div className="fx-shell">
          <aside className="fx-rail">
            <div className="fx-rail-id">
              <span className="fx-rail-avatar">
                <svg width="16" height="17" viewBox="0 0 16 17" fill="none" xmlns="http://www.w3.org/2000/svg">
  <g clip-path="url(#clip0_4_5)">
    <path d="M8 8.5C10.2091 8.5 12 6.70914 12 4.5C12 2.29086 10.2091 0.5 8 0.5C5.79086 0.5 4 2.29086 4 4.5C4 6.70914 5.79086 8.5 8 8.5Z" fill="black" />
    <path d="M0 17C0 12.6 3.6 10 8 10C12.4 10 16 12.6 16 17H0Z" fill="black" />
  </g>
  <defs>
    <clipPath id="clip0_4_5">
      <rect width="16" height="17" fill="white" />
    </clipPath>
  </defs>
</svg>
              </span>
              <div>
                <b>{fullName || "…"}</b>
                {me?.email && fullName !== me.email ? <span>{me.email}</span> : null}
              </div>
            </div>

            <nav aria-label={t("profile.nav.label", "Разделы профиля")}>
              {items.map((it) => (
                <Link
                  key={it.id}
                  to={it.to}
                  className={it.id === activeId ? "on" : ""}
                  aria-current={it.id === activeId ? "page" : undefined}
                  onClick={onNavClick(it.id)}
                >
                  {it.icon}
                  {it.label}
                </Link>
              ))}
            </nav>

            <div className="fx-rail-foot">
              {t("profile.railStatus", "Статус верификации")}
              <br />
              <b className={statusTone || ""}>{statusText || "…"}</b>
            </div>
          </aside>

          <main className="fx-content">
            <div className="fx-inner">{children}</div>
          </main>
        </div>
        {floating}
        {toast}
      </div>
    </div>
  );
}
